/**
 * @file: index.ts
 * @author: eric <xuxiang@zhichetech.com>
 * @copyright: (c) 2019-2020 sichuan zhichetech co., ltd.
 */

import { StatusColors } from '@euler/components/Colors';
import { FontFamily } from '@euler/components/typography';
import { getDefectiveLevelColor } from '@euler/functions/getDefectiveLevel';
import { DefectiveLevel } from '@euler/model';
import { AttributedText } from '@euler/model/AttributedText';
import {
  InspectionTaskEventSubType,
  InspectionTaskEventType,
} from '@euler/model/enum';
import { nanoid } from 'nanoid/non-secure';
import { DefaultTheme } from 'styled-components';
import { registerEventType } from '../../registry';

const register = registerEventType(InspectionTaskEventType.DeliveryCheck);

function pendingIssuesConfirmed() {
  register(InspectionTaskEventSubType.PendingIssuesConfirmed)(item => {
    item.label('遗留问题已确认');
  });
}

function itemReviewed() {
  register(InspectionTaskEventSubType.CheckListItemReviewed)((item, data) => {
    item.label(data.name).medias('m');

    const tagText = [data.text, data.remark].filter(x => x?.trim()).join(': ');
    if (tagText) {
      const color = (theme: DefaultTheme) =>
        getDefectiveLevelColor(
          theme,
          data.code === 'OK' ? DefectiveLevel.Fine : DefectiveLevel.Warning,
        );
      const tagline: (string | AttributedText)[] = [
        {
          text:
            (data.code === 'OK' ? '✅ ' : '❌ ') +
            tagText +
            (data.ur ? ` (${data.ur})` : ''),
          color,
        },
      ];
      item.tagline(tagline);
    }

    if (data.rr?.length) {
      for (const record of data.rr) {
        const builder = item.clone();
        builder
          .key(nanoid())
          .label('👆内返记录👆')
          .medias('m', record)
          .tagline(`责任人: ${record.rp}`)
          .tagline(record.re ? `备注: ${record.re}` : '')
          .author('');
        item.associated(builder.get());
      }
    }
  });
}

function reportGenerated() {
  register(InspectionTaskEventSubType.DeliveryCheckReportGenerated)(
    (item, data) => {
      if (data.i) {
        // independent delivery check
        item.label({
          text: data.go ? '报告已生成' : '推送竣工检验报告',
          color: StatusColors.Success,
          fontFamily: FontFamily.NotoSans.Medium,
        });
      } else {
        item.label({
          text: data.go ? '交车报告已生成' : '推送交车报告',
          color: StatusColors.Success,
          fontFamily: FontFamily.NotoSans.Medium,
        });
      }
    },
  );
}

function started() {
  register(InspectionTaskEventSubType.DeliveryCheckStarted)(item => {
    item.label('开始竣工检验');
  });
}

function finished() {
  register(InspectionTaskEventSubType.DeliveryCheckFinished)(item => {
    item.label({
      text: '完成竣工检验',
      color: StatusColors.Success,
      fontFamily: FontFamily.NotoSans.Medium,
    });
  });
}

function amendRequested() {
  register(InspectionTaskEventSubType.DeliveryCheckReportAmendRequested)(
    item => {
      item.label({
        text: '修改报告',
        color: StatusColors.Info,
        fontFamily: FontFamily.NotoSans.Medium,
      });
    },
  );
}

function reportUpdated() {
  register(InspectionTaskEventSubType.DeliveryCheckReportUpdated)(item => {
    item.label({
      text: '报告已更新',
      color: StatusColors.Info,
      fontFamily: FontFamily.NotoSans.Medium,
    });
  });
}

function orderRollback() {
  register(InspectionTaskEventSubType.Rollback)((item, data) => {
    item.label({
      text: '维修返单',
      fontFamily: FontFamily.NotoSans.Medium,
      color: StatusColors.Warn,
    });
    if (data.reason) {
      item.tagline(`备注: ${data.reason}`);
    }
  });
}

pendingIssuesConfirmed();
itemReviewed();
reportGenerated();
reportUpdated();
orderRollback();
started();
finished();
amendRequested();
